/**
 * Frontend Auth Service
 *
 * Typed API wrapper for all auth endpoints.
 * All requests use credentials: 'include' to send session cookies.
 * The frontend never touches Supabase auth directly.
 */

const BASE = '/api/v1';

/**
 * Error thrown by every auth API call, carrying enough information for callers
 * to tell "the server says you are logged out" apart from "the server did not
 * answer".
 *
 * `status` is the HTTP status, or 0 when the request never produced one — a
 * network failure, a DNS blip, or the fetch being aborted. Zero is explicitly
 * NOT a logout signal.
 */
export class AuthApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AuthApiError';
    this.status = status;
  }

  /**
   * True when the server positively told us this request is unauthenticated.
   * Only these justify dropping the user's client-side session.
   */
  get isUnauthenticated(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /**
   * True when the failure says nothing about whether the user is logged in:
   * unreachable server, gateway error, timeout, rate limit, unparseable body.
   * During a deploy this is what a perfectly valid session looks like.
   */
  get isTransient(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500;
  }
}

async function authFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { ...options.headers as Record<string, string> };
  // Only set Content-Type for requests with a body
  if (options.body) {
    headers['Content-Type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetch(`${BASE}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers,
    });
  } catch (error: any) {
    // Never reached the server at all. Status 0 → transient, not a logout.
    throw new AuthApiError(error?.message || 'Network request failed', 0);
  }

  // Parse defensively. A platform-level error page (DigitalOcean serves HTML
  // during a container swap) is not JSON, and letting response.json() throw
  // here used to surface as an indistinguishable "auth failed".
  let data: any = null;
  let parseFailed = false;
  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch {
    parseFailed = true;
  }

  if (!response.ok) {
    throw new AuthApiError(
      data?.error || data?.message || `HTTP ${response.status}`,
      response.status,
    );
  }

  if (parseFailed || data === null) {
    // 2xx with a body we can't read: treat as an infrastructure problem
    // (proxy/edge interception), not as an auth answer.
    throw new AuthApiError('Malformed response from auth service', 0);
  }

  return data as T;
}

// ─── Response Types ──────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  organizationId: string | null;
  emailVerified: boolean;
}

export interface AuthRole {
  name: string;
  isAdmin: boolean;
  /**
   * Effective app-access for the signed-in user, already resolved server-side
   * as the UNION of ALL their roles (card BnkVHTRg-Aih):
   *   - 'both'       → desktop web + field app (default; admins/dev-emails)
   *   - 'field_only' → field/mobile shell only, desktop blocked
   * Optional for backward-compat with older cached sessions; treated as 'both'
   * when absent (see client/utils/roles.ts `isFieldOnly`).
   */
  appAccess?: 'field_only' | 'both';
}

export interface AuthMeResponse {
  user: AuthUser;
  role: AuthRole;
  permissions: string[];
}

export interface LoginResponse extends AuthMeResponse {}

export interface SignupResponse {
  user: { id: string; email: string; emailVerified: boolean };
  needsVerification: boolean;
}

// ─── Auth API Methods ────────────────────────────────────────

export const authApi = {
  /** POST /auth/login — returns user + role + permissions, sets session cookie */
  login: (email: string, password: string): Promise<LoginResponse> =>
    authFetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  /** POST /auth/signup */
  signup: (email: string, password: string, fullName?: string): Promise<SignupResponse> =>
    authFetch('/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, fullName }),
    }),

  /** GET /auth/me — restore session on page load */
  getCurrentUser: (): Promise<AuthMeResponse> =>
    authFetch('/auth/me'),

  /** GET /auth/session — validate session */
  getSession: (): Promise<{ valid: boolean }> =>
    authFetch('/auth/session'),

  /** POST /auth/logout — deactivate session, clear cookie */
  logout: (): Promise<{ success: boolean }> =>
    authFetch('/auth/logout', { method: 'POST' }),

  /** POST /auth/reset-password */
  resetPassword: (email: string): Promise<{ success: boolean; message: string }> =>
    authFetch('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  /**
   * POST /access-requests — public "Request access" form (card xhNJ9dCqsAzP).
   * Send exactly one of join_code / organization_name, or neither (match by
   * the email's domain). The answer is the same neutral message whether or not
   * an organization matched; only an unknown join code is a 400.
   */
  submitAccessRequest: (body: {
    full_name: string;
    email: string;
    join_code?: string;
    organization_name?: string;
  }): Promise<{ success: boolean; message: string }> =>
    authFetch('/access-requests', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** POST /access-requests/confirm — public, the token from the confirm email. */
  confirmAccessRequest: (token: string): Promise<{ success: boolean; message: string }> =>
    authFetch('/access-requests/confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  /** GET /auth/permissions — force-refresh */
  getPermissions: (): Promise<{ permissions: string[] }> =>
    authFetch('/auth/permissions'),
};
